import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { kaireraAPI } from '../lib/api';
import {
  Loader2, SlidersHorizontal, Target, TrendingUp, Star,
  RotateCcw, Zap, AlertTriangle, Award, Briefcase, Brain
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Cell, Legend
} from 'recharts';

// Grade thresholds (same as backend)
const GRADE_MAP = [
  [9.5, 'A+'], [9.0, 'A'], [8.5, 'A-'], [8.0, 'B+'], [7.5, 'B'],
  [7.0, 'B-'], [6.5, 'C+'], [6.0, 'C'], [5.5, 'C-'], [5.0, 'D+'],
  [4.0, 'D'], [3.0, 'D-'], [0, 'F']
];
const scoreToGrade = (s) => { for (const [t, g] of GRADE_MAP) if (s >= t) return g; return 'F'; };
const gradeColor = (g) => {
  if (!g) return '#6B7280';
  const l = g[0];
  if (l === 'A') return '#16A34A';
  if (l === 'B') return '#84CC16';
  if (l === 'C') return '#EAB308';
  if (l === 'D') return '#F97316';
  return '#DC2626';
};

// Persona classification (simplified from backend logic)
const classifyPersona = (avgScore, topDim) => {
  if (avgScore >= 9) return { name: 'Visionary Leader', icon: Star, color: '#16A34A' };
  if (avgScore >= 8) return { name: 'Strategic Operator', icon: Target, color: '#0D9488' };
  if (avgScore >= 7) return { name: 'Rising Contender', icon: TrendingUp, color: '#3B82F6' };
  if (avgScore >= 6) return { name: 'Building Momentum', icon: Zap, color: '#8B5CF6' };
  return { name: 'Foundation Phase', icon: Brain, color: '#F5841F' };
};

// Career path suggestion based on dominant dimensions
const suggestPath = (dims) => {
  const sorted = [...dims].sort((a, b) => b.simScore - a.simScore);
  const top = sorted.slice(0, 3).map(d => d.id);
  if (top.includes('leadership_readiness') && top.includes('strategic_thinking'))
    return { path: 'C-Suite Executive', salary: '500K-800K', fit: 95 };
  if (top.includes('technical_competency') && top.includes('leadership_readiness'))
    return { path: 'VP of Engineering', salary: '400K-600K', fit: 88 };
  if (top.includes('technical_competency'))
    return { path: 'Principal Engineer / CTO', salary: '350K-550K', fit: 82 };
  if (top.includes('emotional_intelligence') && top.includes('leadership_readiness'))
    return { path: 'Chief People Officer', salary: '380K-520K', fit: 80 };
  if (top.includes('network_visibility') && top.includes('political_social_capital'))
    return { path: 'Business Development Director', salary: '320K-480K', fit: 78 };
  return { path: 'Senior Leadership Role', salary: '350K-500K', fit: 75 };
};

export default function CareerSimulatorPage() {
  const [originalDims, setOriginalDims] = useState([]);
  const [simDims, setSimDims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await kaireraAPI.getReportCard();
        const dims = (res.data.dimensions || [])
          .filter(d => d.score > 0)
          .map(d => ({ ...d, simScore: d.score }));
        setOriginalDims(dims);
        setSimDims(dims);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSliderChange = (dimId, newVal) => {
    setSimDims(prev => prev.map(d => d.id === dimId ? { ...d, simScore: parseFloat(newVal) } : d));
  };

  const resetAll = () => {
    setSimDims(originalDims.map(d => ({ ...d, simScore: d.score })));
  };

  // Computed values
  const computedValues = useMemo(() => {
    if (simDims.length === 0) return null;
    const avg = simDims.reduce((s, d) => s + d.simScore, 0) / simDims.length;
    const origAvg = originalDims.reduce((s, d) => s + d.score, 0) / originalDims.length;
    const grade = scoreToGrade(avg);
    const origGrade = scoreToGrade(origAvg);
    const sorted = [...simDims].sort((a, b) => b.simScore - a.simScore);
    const persona = classifyPersona(avg, sorted[0]?.id);
    const origPersona = classifyPersona(origAvg, [...originalDims].sort((a, b) => b.score - a.score)[0]?.id);
    const path = suggestPath(simDims);
    const origPath = suggestPath(originalDims.map(d => ({ ...d, simScore: d.score })));
    const changed = simDims.filter((d, i) => d.simScore !== originalDims[i]?.score);
    return { avg, origAvg, grade, origGrade, persona, origPersona, path, origPath, changed };
  }, [simDims, originalDims]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#8B5CF6' }} />
    </div>
  );

  if (simDims.length === 0) return (
    <div className="max-w-lg mx-auto mt-20 text-center">
      <SlidersHorizontal className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--t-text-muted)' }} />
      <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--t-text-heading)' }}>Not Enough Data</h2>
      <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>
        Complete your Career Intelligence assessments to unlock the Trajectory Simulator.
      </p>
    </div>
  );

  const cv = computedValues;
  const hasChanges = cv.changed.length > 0;

  // Radar data
  const radarData = simDims.map((d, i) => ({
    dim: d.name.split(' ').slice(0, 2).join(' '),
    Current: originalDims[i]?.score || 0,
    Simulated: d.simScore,
    fullMark: 10,
  }));

  // Bar comparison data
  const barData = simDims.map((d, i) => ({
    name: d.name.split(' ')[0],
    current: originalDims[i]?.score || 0,
    simulated: d.simScore,
  }));

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="career-simulator-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <motion.h1 initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="text-2xl font-black tracking-tight" style={{ color: 'var(--t-text-heading)' }}>
            <SlidersHorizontal className="w-6 h-6 inline mr-2 -mt-1" style={{ color: '#8B5CF6' }} />
            Career Trajectory Simulator
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="text-sm mt-1" style={{ color: 'var(--t-text-muted)' }}>
            Drag sliders to simulate improvements and see your career trajectory shift in real-time.
          </motion.p>
        </div>
        {hasChanges && (
          <Button onClick={resetAll}
            className="h-8 px-3 text-xs rounded-lg gap-1.5"
            style={{ background: 'var(--t-bg-section)', color: 'var(--t-text)', border: '1px solid var(--t-border)' }}
            data-testid="simulator-reset-btn">
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </Button>
        )}
      </div>

      {/* Live Dashboard - Grade, Persona, Path */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Overall Grade */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="nx-card p-5 text-center relative overflow-hidden" data-testid="sim-grade-card">
          {hasChanges && cv.grade !== cv.origGrade && (
            <div className="absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: '#16A34A15', color: '#16A34A' }}>Changed</div>
          )}
          <motion.div key={cv.grade} initial={{ scale: 0.5 }} animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 300 }}>
            <span className="text-5xl font-black" style={{ color: gradeColor(cv.grade) }}>{cv.grade}</span>
          </motion.div>
          <p className="text-lg font-bold mt-1" style={{ color: 'var(--t-text-heading)' }}>{cv.avg.toFixed(1)}/10</p>
          <p className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--t-text-muted)' }}>
            {hasChanges ? 'Simulated Grade' : 'Current Grade'}
          </p>
          {hasChanges && (
            <p className="text-[10px] mt-1" style={{ color: 'var(--t-text-muted)' }}>
              Was: <span className="font-bold" style={{ color: gradeColor(cv.origGrade) }}>{cv.origGrade}</span> ({cv.origAvg.toFixed(1)})
            </p>
          )}
        </motion.div>

        {/* Persona */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="nx-card p-5 text-center relative overflow-hidden" data-testid="sim-persona-card">
          {hasChanges && cv.persona.name !== cv.origPersona.name && (
            <div className="absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: '#8B5CF615', color: '#8B5CF6' }}>Shifted</div>
          )}
          <motion.div key={cv.persona.name} initial={{ scale: 0.5, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 200 }}>
            <cv.persona.icon className="w-10 h-10 mx-auto" style={{ color: cv.persona.color }} />
          </motion.div>
          <p className="text-sm font-black mt-2" style={{ color: cv.persona.color }}>{cv.persona.name}</p>
          <p className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: 'var(--t-text-muted)' }}>
            {hasChanges ? 'Simulated Persona' : 'Current Persona'}
          </p>
          {hasChanges && cv.persona.name !== cv.origPersona.name && (
            <p className="text-[10px] mt-1" style={{ color: 'var(--t-text-muted)' }}>
              Was: <span className="font-bold">{cv.origPersona.name}</span>
            </p>
          )}
        </motion.div>

        {/* Career Path */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="nx-card p-5 text-center relative overflow-hidden" data-testid="sim-path-card">
          {hasChanges && cv.path.path !== cv.origPath.path && (
            <div className="absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: '#3B82F615', color: '#3B82F6' }}>Updated</div>
          )}
          <Briefcase className="w-10 h-10 mx-auto" style={{ color: '#3B82F6' }} />
          <p className="text-sm font-black mt-2" style={{ color: '#3B82F6' }}>{cv.path.path}</p>
          <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>AED {cv.path.salary}</p>
          <div className="mt-2 mx-auto w-20 h-1.5 rounded-full" style={{ background: 'var(--t-border)' }}>
            <motion.div className="h-full rounded-full" style={{ background: '#3B82F6' }}
              animate={{ width: `${cv.path.fit}%` }} transition={{ duration: 0.5 }} />
          </div>
          <p className="text-[9px] font-bold mt-1" style={{ color: 'var(--t-text-muted)' }}>{cv.path.fit}% Fit</p>
        </motion.div>
      </div>

      {/* Sliders + Radar Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Dimension Sliders */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="nx-card p-5" data-testid="sim-sliders">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-4" style={{ color: '#8B5CF6' }}>
            Adjust Your Dimensions
          </p>
          <div className="space-y-4 max-h-[480px] overflow-y-auto pr-2 custom-scroll">
            {simDims.map((d, i) => {
              const orig = originalDims[i]?.score || 0;
              const diff = d.simScore - orig;
              const isChanged = diff !== 0;
              return (
                <div key={d.id} data-testid={`sim-slider-${d.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-semibold" style={{ color: isChanged ? '#8B5CF6' : 'var(--t-text-secondary)' }}>
                      {d.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <motion.span key={d.simScore}
                        initial={{ scale: 1.3 }} animate={{ scale: 1 }}
                        className="text-sm font-black"
                        style={{ color: gradeColor(scoreToGrade(d.simScore)) }}>
                        {d.simScore.toFixed(1)}
                      </motion.span>
                      <span className="text-[9px] font-bold w-6 text-right"
                        style={{ color: gradeColor(scoreToGrade(d.simScore)) }}>
                        {scoreToGrade(d.simScore)}
                      </span>
                      {isChanged && (
                        <span className="text-[9px] font-bold px-1 py-0.5 rounded"
                          style={{ color: diff > 0 ? '#16A34A' : '#DC2626', background: diff > 0 ? '#16A34A10' : '#DC262610' }}>
                          {diff > 0 ? '+' : ''}{diff.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <input type="range" min="0" max="10" step="0.5"
                      value={d.simScore}
                      onChange={e => handleSliderChange(d.id, e.target.value)}
                      className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, ${gradeColor(scoreToGrade(d.simScore))} ${d.simScore * 10}%, var(--t-border) ${d.simScore * 10}%)`,
                        accentColor: gradeColor(scoreToGrade(d.simScore)),
                      }} />
                    {/* Original score marker */}
                    <div className="absolute top-0 h-2 w-0.5 rounded pointer-events-none"
                      style={{
                        left: `${orig * 10}%`,
                        background: 'var(--t-text-muted)',
                        opacity: 0.5,
                      }} />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Radar Comparison */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
          className="nx-card p-5" data-testid="sim-radar-chart">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#0D9488' }}>
            Current vs Simulated
          </p>
          <div style={{ height: 350 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                <PolarGrid stroke="var(--t-border)" />
                <PolarAngleAxis dataKey="dim" tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: 'var(--t-text-muted)', fontSize: 8 }} />
                <Radar name="Current" dataKey="Current" stroke="#6B728088" fill="#6B728010"
                  fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="5 5" />
                <Radar name="Simulated" dataKey="Simulated" stroke="#8B5CF6" fill="#8B5CF6"
                  fillOpacity={0.2} strokeWidth={2.5} animationDuration={500} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip contentStyle={{
                  background: 'var(--t-bg-card)', border: '1px solid var(--t-border)',
                  borderRadius: 10, fontSize: 11,
                }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Before/After Bar Chart */}
      {hasChanges && (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="nx-card p-5" data-testid="sim-bar-comparison">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: '#3B82F6' }}>
            Impact Comparison
          </p>
          <div style={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <YAxis domain={[0, 10]} tick={{ fill: 'var(--t-text-muted)', fontSize: 9 }} />
                <Tooltip contentStyle={{
                  background: 'var(--t-bg-card)', border: '1px solid var(--t-border)',
                  borderRadius: 10, fontSize: 11,
                }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="current" name="Current" fill="#6B728040" radius={[4, 4, 0, 0]} barSize={18} />
                <Bar dataKey="simulated" name="Simulated" fill="#8B5CF6" radius={[4, 4, 0, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Tips */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
        className="nx-card p-5" style={{ borderLeft: '4px solid #8B5CF6' }}
        data-testid="sim-tips">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-4 h-4" style={{ color: '#8B5CF6' }} />
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#8B5CF6' }}>
            How to Use
          </span>
        </div>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>
          Drag any dimension slider to see how improving that area changes your overall grade,
          career persona classification, and recommended career path. Focus on dimensions where
          small improvements create the biggest shifts. The grey marker on each slider shows your
          current score for reference.
        </p>
      </motion.div>
    </div>
  );
}
